import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useMotionValueEvent, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';

import { useVtfLookupSetting } from '../../hooks/useVtfData';

// Thẻ tra cứu VTF chỉ hiện khi bật cấu hình → tải riêng, không nằm trong file chính của trang
const VtfCodeLookupCard = lazy(() => import('../../components/VtfCodeLookupCard'));

// Landing 1MS. Ảnh giao diện: chỉ dùng màn Tổng quan thật (không có dữ liệu cá nhân);
// màn học viên + chat Zalo dựng bằng HTML với dữ liệu minh hoạ để không lộ thông tin võ sinh thật

const Icon = ({ name, className = '' }) => <span className={`material-symbols-outlined ${className}`}>{name}</span>;

const ZaloIcon = ({ className = 'h-6 w-6' }) => (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
        <rect width="48" height="48" rx="12" fill="#0068FF" />
        <text x="24" y="29.5" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="800" fontSize="15" fill="#fff">
            Zalo
        </text>
    </svg>
);

// ───────── Animation kiểu Apple: hiện dần khi cuộn tới, chữ mờ → rõ, ảnh nghiêng theo cuộn.
// Người dùng bật "Giảm chuyển động" trong máy thì mọi thứ hiện ngay, không chuyển động
const EASE = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, y = 40, className = '' }) => {
    const reduce = useReducedMotion();
    return (
        <motion.div
            className={className}
            initial={reduce ? false : { opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: EASE, delay }}
        >
            {children}
        </motion.div>
    );
};

// Tiêu đề hero: từng chữ hiện lên từ mờ nhoè → rõ nét
const BlurWords = ({ text, className = '', delay = 0 }) => {
    const reduce = useReducedMotion();
    return text.split(' ').map((word, i) => (
        <motion.span
            key={`${word}-${i}`}
            className={`inline-block ${className}`}
            initial={reduce ? false : { opacity: 0, y: 24, filter: 'blur(12px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.8, ease: EASE, delay: delay + i * 0.06 }}
        >
            {word}&nbsp;
        </motion.span>
    ));
};

// Câu lớn sáng dần từng chữ theo tiến độ cuộn (như trang sản phẩm Apple)
const ScrollLitText = ({ text }) => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start 85%', 'end 45%'] });
    const words = text.split(' ');
    return (
        // Căn đều hai bên: giữa các chữ là khoảng trắng thường (không phải &nbsp;) để trình duyệt giãn đều được
        <p ref={ref} className="text-justify text-3xl font-extrabold leading-tight text-primary dark:text-white sm:text-5xl lg:text-6xl">
            {words.map((word, i) => (
                <React.Fragment key={`${word}-${i}`}>
                    <LitWord progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]} reduce={reduce}>
                        {word}
                    </LitWord>
                    {i < words.length - 1 ? ' ' : null}
                </React.Fragment>
            ))}
        </p>
    );
};

const LitWord = ({ children, progress, range, reduce }) => {
    const opacity = useTransform(progress, range, [0.15, 1]);
    return (
        <motion.span style={{ opacity: reduce ? 1 : opacity }} className="inline-block">
            {children}
        </motion.span>
    );
};

// iPhone Duo trong hero: mới vào trang thì máy đang gập (thấy màn ngoài), vuốt xuống tới đâu máy mở ra tới đó.
// Ảnh thật: màn trong (Tổng quan) tách 2 nửa theo bản lề giữa ảnh; nửa trái là "lá gập" — mặt trong = nửa trái
// màn trong, mặt ngoài = ảnh máy đang gập. Lá quay quanh bản lề: 180° = gập (úp sang phải), 0° = mở phẳng.
// Khi gập, máy chỉ còn nửa phải nên dịch sang trái 1/4 để nằm giữa khung
const OPEN_IMG = "url('/landing/app-overview.webp'), url('/landing/app-overview.png')";
const FOLDED_IMG = "url('/landing/app-folded.webp'), url('/landing/app-folded.png')";

const halfOf = (side) => ({
    backgroundImage: OPEN_IMG,
    backgroundSize: '200% 100%',
    backgroundPosition: side === 'left' ? 'left center' : 'right center',
    backgroundRepeat: 'no-repeat',
});
const BACKFACE_HIDDEN = { backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' };

// Độ dày mỗi nửa máy so với bề rộng cả máy khi mở
const THICKNESS_RATIO = 0.018;
// Bo góc thân máy đo từ ảnh (~112px trên ảnh 1304×933): ngang tính theo bề rộng nửa máy, dọc theo chiều cao
const CORNER_X = '17.2%';
const CORNER_Y = '12%';
// Thân máy trong ảnh lùi vào so với khung ảnh (nút bấm nhô ra ở cạnh trên / phải)
const BODY_INSET = { top: '0.32%', bottom: '0.2%' };
const shapeOf = (outer) =>
    outer === 'left'
        ? `${CORNER_X} 0 0 ${CORNER_X} / ${CORNER_Y} 0 0 ${CORNER_Y}`
        : `0 ${CORNER_X} ${CORNER_X} 0 / 0 ${CORNER_Y} ${CORNER_Y} 0`;
// Gáy bản lề: kim loại tối ở giữa, sáng nhẹ hai mép
const RAIL = 'linear-gradient(90deg, #4b515a 0%, #1c1f24 35%, #0f1114 60%, #3a3f47 100%)';

// Bề dày một nửa máy: xếp chồng các lớp mỏng cùng hình thân máy (bo góc như máy) dọc theo chiều sâu, nằm giữa mặt
// màn hình và mặt lưng → thành khối liền, góc bo mượt. Màu sáng ở hai mép, tối ở giữa như khung nhôm.
// `outer` = mép không có bản lề ('left' | 'right')
const Extrusion = ({ t, outer }) => {
    const n = Math.max(6, Math.min(16, Math.round(t / 1.1)));
    const radius = shapeOf(outer);
    const sideInset = outer === 'right' ? { left: 0, right: '0.6%' } : { left: 0, right: 0 };
    return Array.from({ length: n }, (_, i) => {
        const k = n === 1 ? 0.5 : i / (n - 1);
        const c = Math.round(70 - 50 * (1 - Math.abs(2 * k - 1))); // 70 ở mép → 20 ở giữa
        return (
            <div
                key={i}
                style={{
                    position: 'absolute',
                    ...BODY_INSET,
                    ...sideInset,
                    borderRadius: radius,
                    background: `rgb(${c}, ${c + 3}, ${c + 9})`,
                    transform: `translateZ(${-t / 2 + (t * (i + 0.5)) / n}px)`,
                }}
            />
        );
    });
};

const HeroDevice = () => {
    const reduce = useReducedMotion();
    const { scrollY } = useScroll();
    // Khoảng cuộn mở máy tính theo vị trí thật của máy: bắt đầu mở khi đỉnh máy lên tới ~giữa màn hình (máy tính: ngay
    // khi bắt đầu cuộn), mở hẳn sau thêm 40% màn hình cuộn → điện thoại / máy tính đều thấy máy gập rồi mở ra
    const deviceRef = useRef(null);
    const range = useRef([40, 520]); // đọc qua ref để hàm biến đổi luôn dùng mốc mới nhất
    useEffect(() => {
        const measure = () => {
            const el = deviceRef.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top + window.scrollY;
            const vh = window.innerHeight;
            const start = Math.max(40, top - vh * 0.55);
            range.current = [start, start + vh * 0.4];
        };
        measure();
        window.addEventListener('resize', measure);
        window.addEventListener('load', measure); // font / ảnh tải xong có thể đẩy máy xuống
        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('load', measure);
        };
    }, []);
    // tiến độ mở 0 → 1 theo cuộn; bấm nút Mở / Gập thì nút điều khiển (cuộn không còn tác dụng).
    // Đi qua lò xo cho mượt (không giật theo từng nấc cuộn chuột)
    const raw = useTransform(scrollY, (v) => {
        const [a, b] = range.current;
        return Math.min(1, Math.max(0, (v - a) / (b - a)));
    });
    const manualOn = useMotionValue(0);
    const manual = useMotionValue(0);
    const target = useTransform([raw, manual, manualOn], ([r, mv, on]) => (on ? mv : r));
    const p = useSpring(target, { stiffness: 140, damping: 28, mass: 0.4 });
    const [isOpen, setIsOpen] = useState(false);
    useMotionValueEvent(p, 'change', (v) => setIsOpen(v > 0.5));
    const toggle = () => {
        manualOn.set(1);
        manual.set(isOpen ? 0 : 1);
    };

    // Độ dày (px) theo bề rộng thật của máy trên màn hình
    const [t, setT] = useState(14);
    useEffect(() => {
        const el = deviceRef.current;
        if (!el) return undefined;
        const ro = new ResizeObserver(([entry]) => setT(Math.max(6, Math.round(entry.contentRect.width * THICKNESS_RATIO))));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const leafRotate = useTransform(p, [0, 1], [180, 0]);
    const x = useTransform(p, [0, 1], ['-25%', '0%']);
    const scale = useTransform(p, [0, 0.5, 1], [0.92, 0.95, 1]);
    // ngả ra sau để thấy bề dày; mở hẳn vẫn giữ nghiêng nhẹ cho có chiều sâu
    const tilt = useTransform(p, [0, 1], [16, 4]);
    const turn = useTransform(p, [0, 1], [-14, 0]); // khi gập xoay nhẹ cho thấy mép ngoài
    const hingeShade = useTransform(p, [0, 0.6, 1], [0.45, 0.25, 0]);
    const leafShade = useTransform(p, [0, 0.5, 1], [0.7, 0.35, 0]);
    const hintOpacity = useTransform(p, [0, 0.3], [1, 0]);
    // Giảm chuyển động: hiện máy mở sẵn, không gắn theo cuộn
    const m = (value, fallback) => (reduce ? fallback : value);
    const half = t / 2;

    return (
        <div className="relative mx-auto mt-14 max-w-5xl" style={{ perspective: 2400 }}>
            <div className="absolute inset-x-10 -bottom-6 top-10 rounded-[3rem] bg-primary/20 blur-3xl" />
            <motion.div
                ref={deviceRef}
                role="img"
                aria-label="iPhone Duo mở ra kiểu quyển sách, màn trong là màn Tổng quan của app 1MS"
                className="relative w-full"
                style={{
                    aspectRatio: '1304 / 933',
                    transformStyle: 'preserve-3d',
                    transformOrigin: 'center bottom',
                    x: m(x, '0%'),
                    scale: m(scale, 1),
                    rotateX: m(tilt, 4),
                    rotateY: m(turn, 0),
                }}
            >
                {/* Nửa phải: khối đứng yên. Mặt trước = nửa phải màn trong, mặt sau = vỏ máy */}
                <div className="absolute inset-y-0 right-0 w-1/2" style={{ transformStyle: 'preserve-3d' }}>
                    <div className="absolute inset-0" style={{ ...halfOf('right'), ...BACKFACE_HIDDEN, transform: `translateZ(${half}px)` }}>
                        <motion.div className="absolute inset-0 bg-gradient-to-r from-black to-transparent" style={{ opacity: m(hingeShade, 0) }} />
                    </div>
                    <div
                        className="absolute inset-x-0 bg-gradient-to-br from-slate-700 via-slate-900 to-black"
                        style={{
                            ...BACKFACE_HIDDEN,
                            transform: `rotateY(180deg) translateZ(${half}px)`,
                            ...BODY_INSET,
                            borderRadius: shapeOf('left'), // mặt lưng lật 180° nên góc bo nằm bên trái của nó
                        }}
                    />
                    <Extrusion t={t} outer="right" />
                </div>

                {/* Gáy bản lề: dải bo tròn ở giữa, lộ ra khi máy gập / đang gập */}
                <div
                    className="absolute"
                    style={{
                        left: '50%',
                        top: CORNER_Y,
                        bottom: CORNER_Y,
                        width: t * 2,
                        borderRadius: 9999,
                        background: RAIL,
                        transform: `translateX(-50%) translateZ(${half}px) rotateY(90deg)`,
                    }}
                />

                {/* Lá gập: nửa trái, quay quanh bản lề đặt ở mép phải và cao hơn mặt máy nửa bề dày →
                    khi gập, lá nằm chồng lên nửa phải (màn úp vào màn) chứ không lồng vào nhau */}
                <motion.div
                    className="absolute inset-y-0 left-0 w-1/2"
                    style={{ transformOrigin: `100% 50% ${half}px`, transformStyle: 'preserve-3d', rotateY: m(leafRotate, 0) }}
                >
                    <div className="absolute inset-0" style={{ ...halfOf('left'), ...BACKFACE_HIDDEN, transform: `translateZ(${half}px)` }}>
                        <motion.div className="absolute inset-0 bg-gradient-to-l from-black/80 to-black/20" style={{ opacity: m(leafShade, 0) }} />
                    </div>
                    {/* Mặt ngoài = ảnh máy đang gập; lật sẵn 180° nên khi lá úp sang phải sẽ hiện đúng chiều */}
                    <div
                        className="absolute inset-0"
                        style={{
                            ...BACKFACE_HIDDEN,
                            transform: `rotateY(180deg) translateZ(${half}px)`,
                            backgroundImage: FOLDED_IMG,
                            backgroundSize: '100% 100%',
                            backgroundRepeat: 'no-repeat',
                        }}
                    />
                    <Extrusion t={t} outer="left" />
                </motion.div>
            </motion.div>
            {!reduce ? (
                <div className="relative mt-6 flex flex-col items-center gap-2">
                    <button
                        type="button"
                        onClick={toggle}
                        aria-pressed={isOpen}
                        className="group flex items-center gap-2 rounded-full bg-white/90 px-5 py-2.5 text-sm font-bold text-primary shadow-lg shadow-primary/10 ring-1 ring-slate-200 backdrop-blur transition hover:-translate-y-0.5 hover:shadow-xl active:scale-95 dark:bg-slate-900/90 dark:text-white dark:ring-slate-700">
                        <Icon name={isOpen ? 'close_fullscreen' : 'open_in_full'} className="text-lg text-accent transition-transform group-hover:scale-110" />
                        {isOpen ? 'Gập máy' : 'Mở máy'}
                    </button>
                    <motion.p className="flex items-center gap-1 text-xs font-medium text-slate-400" style={{ opacity: hintOpacity }}>
                        <Icon name="swipe_down" className="text-sm" /> hoặc vuốt xuống để mở
                    </motion.p>
                </div>
            ) : null}
        </div>
    );
};

// Điện thoại minh hoạ: lơ lửng nhẹ, nghiêng theo cuộn
const FloatingPhone = ({ children }) => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
    const rotate = useTransform(scrollYProgress, [0, 1], [-6, 6]);
    const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
    if (reduce) return <div ref={ref}>{children}</div>;
    return (
        <motion.div ref={ref} style={{ rotate, y }}>
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}>
                {children}
            </motion.div>
        </motion.div>
    );
};

const HIGHLIGHTS = [
    {
        icon: 'how_to_reg',
        title: 'Điểm danh khuôn mặt',
        desc: 'Võ sinh đi ngang camera là được điểm danh, kèm điểm danh thủ công theo ca và ca bù.',
    },
    {
        icon: 'payments',
        title: 'Học phí không thất thoát',
        desc: 'Gói tháng / gói buổi, cảnh báo đi tập chưa đóng phí, đối soát chuyển khoản tự động.',
    },
    { icon: 'smart_toy', title: 'Zalo Bot cho HLV', desc: 'Nhắn tin để thêm võ sinh, xem báo cáo điểm danh, hỏi trợ lý AI về kỹ thuật.' },
];

const FEATURES = [
    {
        icon: 'groups',
        title: 'Hồ sơ võ sinh',
        desc: 'Ảnh, ngày sinh, phụ huynh, cấp đai, lớp chính và lớp học thêm, theo dõi chiều cao – cân nặng.',
    },
    {
        icon: 'event_available',
        title: 'Điểm danh & ca học',
        desc: 'Lịch ca theo từng cơ sở, điểm danh nhanh trên điện thoại hoặc bằng nhận diện khuôn mặt.',
    },
    {
        icon: 'receipt_long',
        title: 'Thu học phí',
        desc: 'Thu theo gói, gia hạn tự động, hoàn tác giao dịch, báo cáo 12 tháng theo từng võ sinh.',
    },
    {
        icon: 'account_balance',
        title: 'Đối soát chuyển khoản',
        desc: 'Giao dịch ngân hàng đổ về qua SePay, ghi nhận vào học phí của võ sinh chỉ với vài chạm.',
    },
    { icon: 'military_tech', title: 'Thi nâng cấp đai', desc: 'Đăng ký dự thi, theo dõi đóng lệ phí, cập nhật kết quả và cấp đai mới.' },
    {
        icon: 'verified',
        title: 'Hội viên VTF',
        desc: 'Tra cứu mã hội viên Liên đoàn, đồng bộ mã VTF cho võ sinh, báo khi có hội viên mới.',
    },
    { icon: 'badge', title: 'Chấm công HLV', desc: 'Chấm công theo ca dạy và cấu hình lương cho huấn luyện viên, nhân sự.' },
    {
        icon: 'inventory_2',
        title: 'Quản lý kho',
        desc: 'Võ phục, đai, giáp: nhập – xuất – báo hỏng, tồn kho và vốn lưu kho theo danh mục.',
    },
    {
        icon: 'monitoring',
        title: 'Báo cáo tổng quan',
        desc: 'Doanh thu, sĩ số đi tập, sinh nhật sắp tới, tỷ lệ theo lớp – tất cả trên một màn hình.',
    },
];

const STEPS = [
    { n: '01', title: 'Tạo CLB & cơ sở', desc: 'Khai báo các cơ sở, ca học và gói học phí của võ đường.' },
    { n: '02', title: 'Thêm võ sinh', desc: 'Nhập trên app, trên web hoặc nhắn qua Zalo Bot cho nhanh.' },
    { n: '03', title: 'Vận hành mỗi ngày', desc: 'Điểm danh, thu phí, theo dõi báo cáo ngay trên điện thoại.' },
];

const TESTIMONIALS = [
    {
        quote: 'Từ khi dùng 1ms, việc điểm danh và theo dõi kỳ thi lên đai của 200 học viên trở nên vô cùng đơn giản. Tôi có thêm thời gian để tập trung chuyên môn.',
        name: 'HLV Nguyễn Văn Cường',
        role: 'Chủ nhiệm CLB Taekwondo Bạch Mai',
    },
    {
        quote: 'Hệ thống 1ms thực sự chuyên nghiệp hóa hình ảnh võ đường của tôi.',
        name: 'HLV Nguyễn Thái Duy',
        role: 'Chủ nhiệm CLB Taekwondo Thanh Phong',
    },
    {
        quote: 'Hệ thống báo cáo tài chính minh bạch giúp tôi quản lý nhiều cơ sở cùng lúc mà không lo thất thoát. Một giải pháp tuyệt vời cho các võ đường hiện đại.',
        name: 'HLV Phan Minh Hoàng',
        role: 'Giám khảo Quốc gia - HLV CLB Nam Phong',
    },
];

const FAQS = [
    {
        q: 'Phần mềm có hỗ trợ quản lý thi lên đai không?',
        a: 'Có. 1ms có sẵn quy trình quản lý kỳ thi lên đai: đăng ký dự thi, theo dõi lệ phí, cập nhật kết quả và cấp bậc mới cho võ sinh.',
    },
    {
        q: 'Có ứng dụng trên điện thoại không?',
        a: 'Có. 1ms có app cho iPhone và Android (đăng nhập bằng tài khoản, Google hoặc Apple), dùng tốt cả trên máy gập và máy tính bảng. Trên máy tính, bạn dùng trực tiếp bản web.',
    },
    {
        q: 'Dữ liệu võ sinh có được bảo mật không?',
        a: 'Chúng tôi sử dụng mã hóa đa tầng và máy chủ lưu trữ an toàn, đảm bảo mọi thông tin cá nhân và lịch sử tập luyện của học viên luôn được bảo mật tuyệt đối.',
    },
];

// Ảnh thật màn Tổng quan trên iPhone (không có dữ liệu cá nhân), đặt trong khung máy
const PhoneShot = () => (
    <div className="relative mx-auto w-[250px] rounded-[2.8rem] bg-slate-900 p-2.5 shadow-2xl shadow-primary/30 ring-1 ring-slate-700 sm:w-[290px]">
        <div className="absolute left-1/2 top-4 z-10 h-6 w-24 -translate-x-1/2 rounded-full bg-black" />
        <picture>
            <source srcSet="/landing/app-phone.webp" type="image/webp" />
            <img
                src="/landing/app-phone.png"
                alt="App 1MS trên iPhone: màn Tổng quan với số liệu CLB, võ sinh đi tập và chưa đóng phí"
                className="block w-full rounded-[2.3rem]"
                width="900"
                height="1957"
                loading="lazy"
            />
        </picture>
    </div>
);

// Bong bóng chat: hiện lần lượt khi cuộn tới, như đang nhắn thật
const ChatBubble = ({ me, delay, children }) => {
    const reduce = useReducedMotion();
    return (
        <motion.div
            initial={reduce ? false : { opacity: 0, y: 16, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, ease: EASE, delay }}
            className={
                me
                    ? 'ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-md bg-[#0068ff] px-3.5 py-2 text-white'
                    : 'w-fit max-w-[90%] rounded-2xl rounded-bl-md bg-white px-3.5 py-2.5 text-slate-700 shadow-sm'
            }
        >
            {children}
        </motion.div>
    );
};

// Khung chat Zalo Bot minh hoạ (dữ liệu giả)
const ZaloChat = () => (
    <div className="mx-auto w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl shadow-primary/20 ring-1 ring-slate-100">
        <div className="flex items-center gap-3 bg-[#0068ff] px-5 py-4 text-white">
            <ZaloIcon className="h-9 w-9 rounded-xl ring-2 ring-white/30" />
            <div>
                <div className="text-sm font-bold">1MS Bot</div>
                <div className="text-[11px] opacity-80">Đang hoạt động</div>
            </div>
        </div>
        <div className="space-y-3 bg-slate-50 p-4 text-[13px]">
            {[
                { me: true, body: '/diemdanh' },
                {
                    me: false,
                    body: (
                        <>
                            <div className="font-bold text-slate-900">📋 Điểm danh hôm nay</div>
                            <div className="mt-1">Cơ sở 1: 18/20 có mặt</div>
                            <div>Cơ sở 2: 12/15 có mặt</div>
                            <div className="mt-1 text-slate-500">Vắng: 5 võ sinh</div>
                        </>
                    ),
                },
                { me: true, body: 'Trần An - 15/06/2015 - 0901234567 - Cơ sở 1' },
                {
                    me: false,
                    body: (
                        <>
                            ✅ Đã tạo hồ sơ võ sinh <b>Trần An</b> tại Cơ sở 1.
                        </>
                    ),
                },
            ].map((m, i) => (
                <ChatBubble key={i} me={m.me} delay={0.25 + i * 0.45}>
                    {m.body}
                </ChatBubble>
            ))}
        </div>
    </div>
);

const SectionTitle = ({ eyebrow, title, desc, center = true }) => (
    <div className={`mb-12 ${center ? 'mx-auto max-w-2xl text-center' : ''}`}>
        <p className="mb-3 text-sm font-bold uppercase tracking-widest text-accent">{eyebrow}</p>
        <h2 className="text-3xl font-extrabold leading-tight text-primary dark:text-white sm:text-4xl">{title}</h2>
        {desc ? <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">{desc}</p> : null}
    </div>
);

const LandingPage = () => {
    const navigate = useNavigate();
    const { data: settingData } = useVtfLookupSetting();
    const lookupEnabled = settingData?.enabled;

    return (
        <>
            {/* HERO */}
            <section className="martial-gradient relative overflow-hidden pt-28 pb-16 lg:pt-36 lg:pb-24">
                <div className="hero-pattern pointer-events-none absolute inset-0" />
                <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-accent/10 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl" />

                <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-6">
                    <div className="mx-auto max-w-3xl text-center">
                        <Reveal y={16}>
                            <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full bg-accent/10 px-4 py-1.5 text-accent">
                                <Icon name="sports_martial_arts" className="text-sm" />
                                <span className="text-xs font-bold uppercase tracking-wider">Phần mềm quản lý võ đường</span>
                            </div>
                        </Reveal>
                        <h1 className="text-[1.75rem] font-extrabold leading-[1.15] text-primary dark:text-white min-[400px]:text-4xl sm:text-5xl lg:text-6xl">
                            <BlurWords text="Quản lý võ đường" />
                            <br className="hidden sm:block" />
                            <BlurWords text="gọn trong lòng bàn tay" className="text-accent" delay={0.2} />
                        </h1>
                        <Reveal delay={0.6} y={20}>
                            <p className="mx-auto mt-6 max-w-2xl text-base text-slate-600 dark:text-slate-400 sm:text-lg">
                                Võ sinh, điểm danh, học phí, thi lên đai, kho võ phục và chấm công HLV — 1MS gom tất cả về một nơi, trên app
                                và trên web, để HLV dành thời gian cho việc dạy võ.
                            </p>
                            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
                                <a
                                    href="tel:0948082110"
                                    className="group flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-8 py-4 text-lg font-bold text-white shadow-xl shadow-accent/30 transition-all hover:scale-105 hover:text-white sm:w-auto"
                                >
                                    <Icon name="call" /> Gọi tư vấn
                                </a>
                                <a
                                    href="https://zalo.me/0948082110"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-primary/15 px-8 py-4 text-lg font-bold text-primary transition-all hover:bg-primary/5 hover:text-primary dark:border-slate-800 dark:text-white sm:w-auto"
                                >
                                    <ZaloIcon /> Nhắn Zalo
                                </a>
                            </div>
                            <p className="mt-6 text-sm font-medium text-slate-500">
                                <Icon name="verified" className="align-middle text-base text-accent" /> 30+ võ đường đã chuyển đổi số cùng
                                1MS
                            </p>
                        </Reveal>
                    </div>

                    {/* Ảnh giao diện thật: màn Tổng quan trên máy gập, dựng thẳng dần khi cuộn */}
                    <HeroDevice />
                </div>
            </section>

            {/* 3 ĐIỂM NỔI BẬT */}
            <section className="border-y border-slate-100 bg-white py-14 dark:border-slate-800 dark:bg-background-dark">
                <div className="mx-auto grid max-w-7xl gap-8 px-5 sm:px-6 md:grid-cols-3">
                    {HIGHLIGHTS.map((h, i) => (
                        <Reveal key={h.title} delay={i * 0.12} className="flex gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent/10 text-accent">
                                <Icon name={h.icon} className="text-2xl" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-primary dark:text-white">{h.title}</h3>
                                <p className="mt-1 text-slate-600 dark:text-slate-400">{h.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* CÂU LỚN SÁNG DẦN THEO CUỘN */}
            <section className="bg-white py-24 dark:bg-background-dark sm:py-32">
                <div className="mx-auto max-w-5xl px-5 sm:px-6">
                    <ScrollLitText text="Võ sinh, điểm danh, học phí, thi đai, kho và chấm công. Một hệ thống. Mọi thiết bị. Để thầy cô tập trung vào việc dạy võ." />
                </div>
            </section>

            {/* TÍNH NĂNG */}
            <section className="bg-slate-50 py-20 dark:bg-slate-900/20 sm:py-24" id="features">
                <div className="mx-auto max-w-7xl px-5 sm:px-6">
                    <Reveal>
                        <SectionTitle
                            eyebrow="Tính năng"
                            title="Mọi việc của võ đường, trong một hệ thống"
                            desc="Thiết kế riêng cho võ đường Taekwondo và các môn võ có hệ thống cấp đai."
                        />
                    </Reveal>
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {FEATURES.map((f, i) => (
                            <Reveal key={f.title} delay={(i % 3) * 0.1} className="h-full">
                                <div className="group h-full rounded-2xl border border-slate-100 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900/60">
                                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white dark:text-white">
                                        <Icon name={f.icon} className="text-2xl" />
                                    </div>
                                    <h3 className="mb-2 text-lg font-bold text-primary dark:text-white">{f.title}</h3>
                                    <p className="text-slate-600 dark:text-slate-400">{f.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* APP ĐIỆN THOẠI */}
            <section className="overflow-hidden bg-white py-20 dark:bg-background-dark sm:py-24" id="app">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-6 lg:grid-cols-2">
                    <Reveal>
                        <SectionTitle
                            center={false}
                            eyebrow="App iPhone & Android"
                            title="Dạy ở cơ sở nào cũng có đủ thông tin trong tay"
                            desc="Tra hồ sơ võ sinh, gọi phụ huynh, điểm danh và thu phí ngay trên sân tập. Giao diện tối ưu cho điện thoại, máy tính bảng và cả máy gập."
                        />
                        <ul className="space-y-4">
                            {[
                                ['touch_app', 'Một chạm để gọi phụ huynh, thu phí hoặc xem lịch sử đóng tiền.'],
                                ['notifications', 'Chuông thông báo khi có võ sinh mới trên hệ thống VTF.'],
                                ['lock', 'Đăng nhập bằng tài khoản, Google hoặc Apple — phân quyền Admin, Quản lý, HLV.'],
                            ].map(([icon, text]) => (
                                <li key={text} className="flex items-start gap-3">
                                    <Icon name={icon} className="mt-0.5 text-accent" />
                                    <span className="text-slate-700 dark:text-slate-300">{text}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <div className="relative">
                        <div className="absolute inset-0 m-auto h-72 w-72 rounded-full bg-accent/15 blur-3xl" />
                        <div className="relative">
                            <FloatingPhone>
                                <PhoneShot />
                            </FloatingPhone>
                        </div>
                    </div>
                </div>
            </section>

            {/* ZALO BOT */}
            <section className="bg-primary py-20 text-white sm:py-24" id="zalo">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-6 lg:grid-cols-2">
                    <div className="order-2 lg:order-1">
                        <ZaloChat />
                    </div>
                    <Reveal className="order-1 lg:order-2">
                        <p className="mb-3 text-sm font-bold uppercase tracking-widest text-accent">Zalo Bot</p>
                        <h2 className="text-3xl font-extrabold leading-tight sm:text-4xl">
                            Quản lý ngay trong Zalo bạn đang dùng hằng ngày
                        </h2>
                        <p className="mt-4 text-lg text-slate-300">
                            Liên kết tài khoản 1MS với Zalo một lần, sau đó HLV chỉ cần nhắn tin cho bot.
                        </p>
                        <div className="mt-8 grid gap-4 sm:grid-cols-2">
                            {[
                                ['/diemdanh', 'Báo cáo có mặt / vắng tại các cơ sở hôm nay'],
                                ['Tên - Ngày sinh - SĐT - Cơ sở', 'Tạo nhanh hồ sơ võ sinh mới'],
                                ['/tim <từ khóa>', 'Tìm võ sinh theo tên, SĐT, mã VTF'],
                                ['/ai <câu hỏi>', 'Hỏi trợ lý AI về kỹ thuật, bài quyền, luật thi đấu'],
                            ].map(([cmd, desc]) => (
                                <div key={cmd} className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
                                    <code className="text-sm font-bold text-accent">{cmd}</code>
                                    <p className="mt-1 text-sm text-slate-300">{desc}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CỔNG PHỤ HUYNH */}
            <section
                className="bg-gradient-to-b from-orange-50/60 to-white py-20 dark:from-slate-900/40 dark:to-background-dark sm:py-24"
                id="phu-huynh"
            >
                <div className="mx-auto max-w-7xl px-5 sm:px-6">
                    <Reveal>
                        <div className="grid items-center gap-10 overflow-hidden rounded-[2rem] border border-orange-100 bg-white p-8 shadow-xl shadow-orange-100/50 dark:border-slate-800 dark:bg-slate-900 sm:p-12 lg:grid-cols-[1.2fr_1fr]">
                            <div>
                                <p className="mb-3 text-sm font-bold uppercase tracking-widest text-accent">Dành cho phụ huynh</p>
                                <h2 className="text-3xl font-extrabold leading-tight text-primary dark:text-white sm:text-4xl">
                                    Theo dõi hành trình võ đạo của con
                                </h2>
                                <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
                                    Không cần tài khoản: nhập tên võ sinh và chọn câu lạc bộ để xem ngay.
                                </p>
                                <button
                                    onClick={() => navigate('/tra-cuu-phu-huynh')}
                                    className="group mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-8 py-4 text-lg font-bold text-white shadow-xl shadow-accent/30 transition-all hover:scale-105 sm:w-auto"
                                >
                                    <Icon name="family_restroom" /> Vào cổng phụ huynh
                                    <Icon name="arrow_forward" className="transition-transform group-hover:translate-x-1" />
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    ['event_available', 'Điểm danh', 'Các buổi con đi tập'],
                                    ['receipt_long', 'Học phí', 'Gói đang học, hạn đóng'],
                                    ['monitoring', 'Phát triển', 'Chiều cao, cân nặng'],
                                    ['emoji_events', 'Thành tích', 'Kỳ thi, cấp đai'],
                                ].map(([icon, title, desc], i) => (
                                    <Reveal key={title} delay={0.15 + i * 0.08} y={24}>
                                        <div className="h-full rounded-2xl bg-slate-50 p-4 dark:bg-slate-800/60">
                                            <Icon name={icon} className="text-2xl text-accent" />
                                            <p className="mt-2 font-bold text-primary dark:text-white">{title}</p>
                                            <p className="text-sm text-slate-500">{desc}</p>
                                        </div>
                                    </Reveal>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CÁCH BẮT ĐẦU */}
            <section className="bg-white py-20 dark:bg-background-dark sm:py-24">
                <div className="mx-auto max-w-7xl px-5 sm:px-6">
                    <Reveal>
                        <SectionTitle eyebrow="Bắt đầu" title="Đi vào vận hành chỉ sau một buổi" />
                    </Reveal>
                    <div className="grid gap-6 md:grid-cols-3">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.12} className="h-full">
                                <div className="relative h-full rounded-2xl border border-slate-100 p-7 dark:border-slate-800">
                                    <span className="text-5xl font-extrabold text-accent/20">{s.n}</span>
                                    <h3 className="mt-2 text-lg font-bold text-primary dark:text-white">{s.title}</h3>
                                    <p className="mt-1 text-slate-600 dark:text-slate-400">{s.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TRA CỨU VTF (bật / tắt theo cấu hình) */}
            {lookupEnabled && (
                <>
                    <section className="relative z-20 overflow-hidden border-y border-slate-100 bg-slate-50/30 px-6 py-20">
                        <div
                            className="pointer-events-none absolute inset-0 z-0 opacity-10"
                            style={{
                                backgroundImage: "url('/nen.png')",
                                backgroundSize: 'cover',
                                backgroundPosition: 'top center',
                                filter: 'grayscale(50%)',
                            }}
                        />
                        <div className="relative z-10 mx-auto flex max-w-7xl justify-center">
                            <Suspense fallback={<div className="h-64" />}>
                                <VtfCodeLookupCard />
                            </Suspense>
                        </div>
                    </section>
                </>
            )}

            {/* CẢM NHẬN */}
            <section className="bg-slate-50 py-20 dark:bg-slate-900/20 sm:py-24" id="testimonials">
                <div className="mx-auto max-w-7xl px-5 sm:px-6">
                    <Reveal>
                        <SectionTitle eyebrow="Cảm nhận" title="Trải nghiệm từ các Võ sư" />
                    </Reveal>
                    <div className="grid gap-6 md:grid-cols-3">
                        {TESTIMONIALS.map((t, i) => (
                            <Reveal key={t.name} delay={i * 0.12} className="h-full">
                                <figure className="flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-7 dark:border-slate-800 dark:bg-slate-900">
                                    <Icon name="format_quote" className="text-4xl text-accent/40" />
                                    <blockquote className="mt-2 flex-1 text-slate-700 dark:text-slate-300">"{t.quote}"</blockquote>
                                    <figcaption className="mt-6 flex items-center gap-3">
                                        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 font-bold text-primary dark:text-white">
                                            {t.name.split(' ').slice(-1)[0][0]}
                                        </div>
                                        <div>
                                            <p className="font-bold text-primary dark:text-white">{t.name}</p>
                                            <p className="text-xs font-bold uppercase tracking-tight text-slate-500">{t.role}</p>
                                        </div>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-white py-20 dark:bg-background-dark sm:py-24" id="faq">
                <div className="mx-auto max-w-3xl px-5 sm:px-6">
                    <Reveal>
                        <SectionTitle eyebrow="Hỏi đáp" title="Câu hỏi thường gặp" />
                    </Reveal>
                    <div className="space-y-4">
                        {FAQS.map((f, i) => (
                            <details
                                key={f.q}
                                open={i === 0}
                                className="group rounded-xl border border-slate-100 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-900/50"
                            >
                                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-bold text-primary dark:text-white">
                                    {f.q}
                                    <Icon name="expand_more" className="transition-transform group-open:rotate-180" />
                                </summary>
                                <p className="mt-4 leading-relaxed text-slate-600 dark:text-slate-400">{f.a}</p>
                            </details>
                        ))}
                        <details className="group rounded-xl border border-slate-100 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-900/50">
                            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-bold text-primary dark:text-white">
                                Phụ huynh có thể theo dõi tiến độ của con không?
                                <Icon name="expand_more" className="transition-transform group-open:rotate-180" />
                            </summary>
                            <div className="mt-4 leading-relaxed text-slate-600 dark:text-slate-400">
                                <p className="mb-4">
                                    Có, 1ms cung cấp cổng thông tin dành riêng cho phụ huynh để xem lịch tập, điểm danh và quá trình tiến bộ
                                    của con em mình.
                                </p>
                                <button
                                    onClick={() => navigate('/tra-cuu-phu-huynh')}
                                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white transition-all hover:bg-blue-700"
                                >
                                    <Icon name="search" className="text-sm" />
                                    Truy cập cổng phụ huynh
                                </button>
                            </div>
                        </details>
                    </div>
                </div>
            </section>

            {/* KÊU GỌI */}
            <section className="px-5 py-16 sm:px-6 sm:py-20">
                <Reveal>
                    <div className="relative mx-auto max-w-5xl overflow-hidden rounded-[2rem] bg-primary p-8 text-center text-white sm:rounded-[2.5rem] sm:p-12">
                        <div className="absolute right-0 top-0 -mr-32 -mt-32 h-64 w-64 rounded-full bg-accent/20 blur-3xl" />
                        <div className="absolute bottom-0 left-0 -mb-32 -ml-32 h-64 w-64 rounded-full bg-white/5 blur-3xl" />
                        <h2 className="relative z-10 mb-5 text-3xl font-extrabold sm:text-4xl">Sẵn sàng hiện đại hóa võ đường của bạn?</h2>
                        <p className="relative z-10 mx-auto mb-10 max-w-2xl text-lg text-slate-300">
                            Tham gia cùng các võ sư đang dùng 1MS để quản lý nhẹ nhàng hơn và tập trung vào chất lượng đào tạo.
                        </p>
                        <div className="relative z-10 flex flex-col justify-center gap-4 sm:flex-row">
                            <a
                                href="tel:0948082110"
                                className="flex items-center justify-center gap-2 rounded-xl bg-accent px-10 py-4 text-lg font-bold text-white shadow-lg shadow-accent/20 transition-all hover:scale-105 hover:text-white"
                            >
                                <Icon name="call" /> Gọi tư vấn
                            </a>
                            <a
                                href="https://zalo.me/0948082110"
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-center gap-2 rounded-xl border border-white/30 px-10 py-4 text-lg font-bold text-white transition-all hover:bg-white/10 hover:text-white"
                            >
                                <ZaloIcon /> Liên hệ Zalo
                            </a>
                        </div>
                    </div>
                </Reveal>
            </section>
        </>
    );
};

export default LandingPage;
